import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../lib/api';
import { CheckCircle2, XCircle, Loader2, Chrome, Shield } from 'lucide-react';

type AuthState = 'loading' | 'login_required' | 'authorizing' | 'success' | 'error';

export function ExtensionAuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuthStore();
  
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  
  const extensionId = searchParams.get('extensionId');
  const redirectUri = searchParams.get('redirect');
  
  useEffect(() => {
    if (!isAuthenticated) {
      setAuthState('login_required');
    } else {
      initiateAuth();
    }
  }, [isAuthenticated]);
  
  useEffect(() => {
    if (authState === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [authState, countdown]);
  
  async function initiateAuth() {
    if (!token) {
      setAuthState('login_required');
      return;
    }
    
    setAuthState('authorizing');
    
    try {
      const response = await api.post('/auth/extension/initiate', {
        extensionId,
        redirectUri,
        scopes: [
          'profile:read',
          'projects:read',
          'projects:write',
          'epics:read',
          'epics:write',
          'requests:read',
          'requests:write',
        ],
      });
      
      const { code } = response as { code: string };
      
      // Redirect back to extension with code
      if (redirectUri) {
        const redirectUrl = `${redirectUri}?code=${code}`;
        setAuthState('success');
        
        // Redirect after countdown
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 3000);
      } else if (extensionId) {
        // Try chrome-extension:// redirect
        const chromeRedirect = `chrome-extension://${extensionId}/auth-callback.html?code=${code}`;
        setAuthState('success');
        
        setTimeout(() => {
          window.location.href = chromeRedirect;
        }, 3000);
      } else {
        // No redirect - show code for manual copy
        setAuthState('success');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authorization failed');
      setAuthState('error');
    }
  }
  
  function handleLoginRedirect() {
    // Save return URL and redirect to login
    const returnUrl = `/auth/extension${window.location.search}`;
    sessionStorage.setItem('extensionAuthReturn', returnUrl);
    navigate('/login');
  }
  
  // Check for return from login
  useEffect(() => {
    const returnUrl = sessionStorage.getItem('extensionAuthReturn');
    if (returnUrl && isAuthenticated) {
      sessionStorage.removeItem('extensionAuthReturn');
      navigate(returnUrl, { replace: true });
    }
  }, [isAuthenticated, navigate]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-950 via-surface-900 to-accent-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="card text-center">
          {/* Header */}
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-accent-500/20 rounded-2xl flex items-center justify-center">
              <Chrome className="w-8 h-8 text-accent-400" />
            </div>
            <h1 className="text-xl font-display font-semibold text-surface-100">
              Connect BA Agent Extension
            </h1>
            <p className="text-sm text-surface-400 mt-2">
              Authorize the extension to sync with your backoffice
            </p>
          </div>
          
          {/* States */}
          {authState === 'loading' && (
            <div className="py-8">
              <Loader2 className="w-12 h-12 mx-auto text-accent-500 animate-spin" />
              <p className="text-surface-400 mt-4">Checking authentication...</p>
            </div>
          )}
          
          {authState === 'login_required' && (
            <div className="py-6 space-y-4">
              <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                <Shield className="w-10 h-10 mx-auto text-surface-400 mb-3" />
                <p className="text-surface-300 mb-4">
                  Please sign in to authorize the extension
                </p>
              </div>
              
              <button
                onClick={handleLoginRedirect}
                className="btn btn-primary w-full"
              >
                Sign in to Continue
              </button>
            </div>
          )}
          
          {authState === 'authorizing' && (
            <div className="py-8">
              <Loader2 className="w-12 h-12 mx-auto text-accent-500 animate-spin" />
              <p className="text-surface-400 mt-4">Generating authorization code...</p>
            </div>
          )}
          
          {authState === 'success' && (
            <div className="py-6">
              <CheckCircle2 className="w-16 h-16 mx-auto text-success-400" />
              <h2 className="text-lg font-semibold text-surface-100 mt-4">
                Authorization Successful!
              </h2>
              <p className="text-surface-400 mt-2">
                Redirecting to extension in {countdown}...
              </p>
              
              {user && (
                <div className="mt-6 p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                  <p className="text-sm text-surface-400">Signed in as</p>
                  <p className="text-surface-200 font-medium">{user.email}</p>
                </div>
              )}
            </div>
          )}
          
          {authState === 'error' && (
            <div className="py-6">
              <XCircle className="w-16 h-16 mx-auto text-danger-400" />
              <h2 className="text-lg font-semibold text-surface-100 mt-4">
                Authorization Failed
              </h2>
              <p className="text-danger-400 mt-2">{error}</p>
              
              <button
                onClick={() => initiateAuth()}
                className="btn btn-secondary mt-6"
              >
                Try Again
              </button>
            </div>
          )}
          
          {/* Permissions Info */}
          {(authState === 'login_required' || authState === 'authorizing') && (
            <div className="mt-6 pt-6 border-t border-surface-800">
              <p className="text-xs text-surface-500 mb-3">The extension will be able to:</p>
              <ul className="text-xs text-surface-400 space-y-1">
                <li>• View your profile and organizations</li>
                <li>• Read and create projects</li>
                <li>• Read and create epics</li>
                <li>• Read and create requests</li>
              </ul>
            </div>
          )}
          
          {/* Extension ID info */}
          {extensionId && (
            <div className="mt-6 pt-6 border-t border-surface-800">
              <p className="text-xs text-surface-500">
                Extension ID: <code className="text-surface-400">{extensionId}</code>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

